import React, { useState, useEffect } from 'react';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import './AdminComponents.css';

interface ProviderStatus {
  name: string;
  enabled?: boolean;
  isConfigured: boolean;
  isActive: boolean;
  hasValidCredentials: boolean;
  baseUrl?: string;
  apiKeyConfigured: boolean;
  apiSecretConfigured: boolean;
  apiPassphraseConfigured?: boolean;
  lastConnectionTest?: string;
  connectionTestStatus: 'SUCCESS' | 'FAILED' | 'NOT_TESTED';
}

interface ProviderStatusResponse {
  currentProvider: string;
  openPositionsCount: number;
  canSwitch: boolean;
  providers: {
    KUCOIN: ProviderStatus;
    SAGEMASTER: ProviderStatus;
  };
}

const TradingProviderSelection: React.FC = () => {
  const [status, setStatus] = useState<ProviderStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const adminApi = useAdminApiService();

  useEffect(() => {
    loadProviderStatus();
  }, []);

  const loadProviderStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApi.getTradingProviderStatus();
      
      if (response.success && response.data) {
        setStatus(response.data);
      } else {
        setError(response.error || 'Failed to load provider status');
      }
    } catch (err: any) {
      console.error('Error loading provider status:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load provider status');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProvider = async (provider: string) => {
    if (!status) return;

    // Check if provider is configured
    const providerStatus = status.providers[provider as keyof typeof status.providers];
    if (!providerStatus?.isConfigured) {
      setError(`${provider} is not configured. Please configure API credentials first.`);
      return;
    }

    // Check for open positions
    if (status.openPositionsCount > 0) {
      const confirmed = window.confirm(
        `Warning: You have ${status.openPositionsCount} open position(s). ` +
        `Switching providers will only affect future trades. Existing positions will remain on the current provider. ` +
        `Do you want to continue?`
      );
      if (!confirmed) return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);
      
      const response = await adminApi.setTradingProvider(provider, false);
      
      if (response.success && response.data) {
        setSuccessMessage(`Successfully switched to ${provider}`);
        // Reload status to get updated info
        await loadProviderStatus();
        
        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      } else {
        setError(response.error || `Failed to switch to ${provider}`);
      }
    } catch (err: any) {
      console.error('Error switching provider:', err);
      setError(err.response?.data?.message || err.message || `Failed to switch to ${provider}`);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (provider: string) => {
    try {
      setTesting(provider);
      setError(null);
      
      const response = await adminApi.testTradingProviderConnection(provider);
      
      if (response.success && response.data) {
        const testResult = response.data;
        if (testResult.status === 'SUCCESS') {
          setSuccessMessage(`Connection test successful for ${provider}`);
        } else {
          setError(`Connection test failed for ${provider}: ${testResult.message || 'Unknown error'}`);
        }
        // Reload status to get updated connection test info
        await loadProviderStatus();
        
        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      } else {
        setError(response.error || `Failed to test connection for ${provider}`);
      }
    } catch (err: any) {
      console.error('Error testing connection:', err);
      setError(err.response?.data?.message || err.message || `Failed to test connection for ${provider}`);
    } finally {
      setTesting(null);
    }
  };

  const handleConfigureProvider = (provider: string) => {
    // Navigate to provider config page with filter
    window.location.href = `/admin/dashboard?tab=providerConfig&provider=${provider}`;
  };

  if (loading) {
    return (
      <div className="admin-section">
        <div className="admin-section-header">
          <h2>Trading Provider Selection</h2>
        </div>
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (error && !status) {
    return (
      <div className="admin-section">
        <div className="admin-section-header">
          <h2>Trading Provider Selection</h2>
        </div>
        <div className="admin-component-error">
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
            <button className="error-close" onClick={() => setError(null)}>×</button>
          </div>
          <button className="retry-btn" onClick={loadProviderStatus}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="admin-section">
        <div className="admin-section-header">
          <h2>Trading Provider Selection</h2>
        </div>
        <div className="admin-component-error">
          <p>No provider status data available</p>
          <button className="retry-btn" onClick={loadProviderStatus}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const kucoinStatus = status.providers.KUCOIN;
  const sagemasterStatus = status.providers.SAGEMASTER;
  const currentProvider = status.currentProvider;

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2>Trading Provider Selection</h2>
        <div className="admin-section-actions">
          <button 
            onClick={loadProviderStatus} 
            className="admin-button secondary"
            disabled={loading}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="success-message" style={{
          padding: '12px 16px',
          backgroundColor: '#f0fdf4',
          border: '1px solid #86efac',
          borderRadius: '8px',
          color: '#166534',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>✓</span>
          <span>{successMessage}</span>
          <button 
            onClick={() => setSuccessMessage(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#166534',
              fontSize: '18px',
              cursor: 'pointer',
              marginLeft: 'auto'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
          <button className="error-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Open Positions Warning */}
      {status.openPositionsCount > 0 && (
        <div style={{
          padding: '16px',
          backgroundColor: '#fef3c7',
          border: '1px solid #fbbf24',
          borderRadius: '8px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <span style={{ fontSize: '20px' }}>⚠️</span>
            <h4 style={{ margin: 0, color: '#92400e' }}>Open Positions Detected</h4>
          </div>
          <p style={{ margin: 0, color: '#78350f' }}>
            You have <strong>{status.openPositionsCount}</strong> open position(s). 
            Switching providers will only affect future trades. Existing positions will remain on the current provider ({currentProvider}).
          </p>
        </div>
      )}

      {/* Provider Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '24px',
        marginTop: '24px'
      }}>
        {/* Kucoin Provider Card */}
        <div className="provider-card" style={{
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '24px',
          backgroundColor: '#ffffff'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0 }}>Kucoin</h3>
            {currentProvider === 'KUCOIN' && (
              <span style={{
                padding: '4px 12px',
                backgroundColor: '#3b82f6',
                color: 'white',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                Current
              </span>
            )}
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: kucoinStatus.isConfigured ? '#10b981' : '#ef4444'
              }}></span>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>
                Status: {kucoinStatus.isConfigured ? 'Configured' : 'Not Configured'}
              </span>
            </div>
            <div style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '16px' }}>
              {kucoinStatus.isActive ? 'Active' : 'Inactive'} • 
              {kucoinStatus.hasValidCredentials ? ' Valid Credentials' : ' Invalid Credentials'}
            </div>
            {kucoinStatus.connectionTestStatus !== 'NOT_TESTED' && (
              <div style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '16px', marginTop: '4px' }}>
                Last Test: {kucoinStatus.connectionTestStatus}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
              Configuration:
            </div>
            <div style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '8px' }}>
              API Key: {kucoinStatus.apiKeyConfigured ? '✓' : '✗'}<br />
              API Secret: {kucoinStatus.apiSecretConfigured ? '✓' : '✗'}<br />
              API Passphrase: {kucoinStatus.apiPassphraseConfigured ? '✓' : '✗'}
            </div>
            {!kucoinStatus.isConfigured && (
              <div style={{ 
                marginTop: '8px', 
                padding: '8px', 
                backgroundColor: '#fef3c7', 
                borderRadius: '4px',
                fontSize: '11px',
                color: '#78350f'
              }}>
                ⚠️ Missing: {!kucoinStatus.apiKeyConfigured && 'API Key '}
                {!kucoinStatus.apiSecretConfigured && 'API Secret '}
                {!kucoinStatus.apiPassphraseConfigured && 'API Passphrase '}
                - Click "Configure" to add missing credentials
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={() => handleTestConnection('KUCOIN')}
              disabled={!kucoinStatus.isConfigured || testing === 'KUCOIN'}
              className="admin-button secondary"
              style={{ flex: 1, minWidth: '120px' }}
              title={!kucoinStatus.isConfigured ? 'Configure provider first to test connection' : ''}
            >
              {testing === 'KUCOIN' ? 'Testing...' : 'Test Connection'}
            </button>
            
            {!kucoinStatus.isConfigured && (
              <button
                onClick={() => handleConfigureProvider('KUCOIN')}
                className="admin-button primary"
                style={{ flex: 1, minWidth: '120px' }}
              >
                Configure
              </button>
            )}
            
            {currentProvider !== 'KUCOIN' && kucoinStatus.isConfigured && (
              <button
                onClick={() => handleSelectProvider('KUCOIN')}
                disabled={saving}
                className="admin-button primary"
                style={{ flex: 1, minWidth: '120px' }}
              >
                {saving ? 'Switching...' : 'Select Provider'}
              </button>
            )}
          </div>
        </div>

        {/* Sagemaster Provider Card */}
        <div className="provider-card" style={{
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '24px',
          backgroundColor: '#ffffff'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0 }}>Sagemaster</h3>
            {currentProvider === 'SAGEMASTER' && (
              <span style={{
                padding: '4px 12px',
                backgroundColor: '#3b82f6',
                color: 'white',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                Current
              </span>
            )}
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: sagemasterStatus.isConfigured ? '#10b981' : '#ef4444'
              }}></span>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>
                Status: {sagemasterStatus.isConfigured ? 'Configured' : 'Not Configured'}
              </span>
            </div>
            <div style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '16px' }}>
              {sagemasterStatus.isActive ? 'Active' : 'Inactive'} • 
              {sagemasterStatus.hasValidCredentials ? ' Valid Credentials' : ' Invalid Credentials'}
            </div>
            {sagemasterStatus.connectionTestStatus !== 'NOT_TESTED' && (
              <div style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '16px', marginTop: '4px' }}>
                Last Test: {sagemasterStatus.connectionTestStatus}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
              Configuration:
            </div>
            <div style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '8px' }}>
              API Key: {sagemasterStatus.apiKeyConfigured ? '✓' : '✗'}<br />
              API Secret: {sagemasterStatus.apiSecretConfigured ? '✓' : '✗'}<br />
              Base URL: {sagemasterStatus.baseUrl ? '✓' : '✗'}
            </div>
            {!sagemasterStatus.isConfigured && (
              <div style={{ 
                marginTop: '8px', 
                padding: '8px', 
                backgroundColor: '#fef3c7', 
                borderRadius: '4px',
                fontSize: '11px',
                color: '#78350f'
              }}>
                ⚠️ Missing: {!sagemasterStatus.apiKeyConfigured && 'API Key '}
                {!sagemasterStatus.apiSecretConfigured && 'API Secret '}
                {!sagemasterStatus.baseUrl && 'Base URL '}
                - Click "Configure" to add missing credentials
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={() => handleTestConnection('SAGEMASTER')}
              disabled={!sagemasterStatus.isConfigured || testing === 'SAGEMASTER'}
              className="admin-button secondary"
              style={{ flex: 1, minWidth: '120px' }}
              title={!sagemasterStatus.isConfigured ? 'Configure provider first to test connection' : ''}
            >
              {testing === 'SAGEMASTER' ? 'Testing...' : 'Test Connection'}
            </button>
            
            {!sagemasterStatus.isConfigured && (
              <button
                onClick={() => handleConfigureProvider('SAGEMASTER')}
                className="admin-button primary"
                style={{ flex: 1, minWidth: '120px' }}
              >
                Configure
              </button>
            )}
            
            {currentProvider !== 'SAGEMASTER' && sagemasterStatus.isConfigured && (
              <button
                onClick={() => handleSelectProvider('SAGEMASTER')}
                disabled={saving}
                className="admin-button primary"
                style={{ flex: 1, minWidth: '120px' }}
              >
                {saving ? 'Switching...' : 'Select Provider'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Info Section */}
      <div style={{
        marginTop: '32px',
        padding: '16px',
        backgroundColor: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '8px'
      }}>
        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>About Trading Providers</h4>
        <p style={{ margin: 0, fontSize: '12px', color: '#6b7280', lineHeight: '1.5' }}>
          Select which trading provider to use for executing trades. The selected provider will be used for all new trading operations.
          Existing open positions will remain on their current provider. Make sure to configure API credentials before selecting a provider.
        </p>
      </div>
    </div>
  );
};

export default TradingProviderSelection;

